import { BRIDGE_SEATS, type BridgeControllers, type BridgeSeat as Seat } from '../bridge/seats.js';
import {
  type ClaimOptions,
  type Discarded,
  type MahjongGame,
  type MahjongPhase,
  type MahjongResult,
  canSelfWin,
  currentClaimOptions,
  mahjongDiscardable,
  kongTiles,
  seatWind,
} from './play.js';
import type { MahjongMeld, MahjongWind } from './score.js';
import type { Face } from '../mahjong-solitaire/tiles.js';

/** A meld as another seat sees it: a concealed kong is only a face-down count, never its tiles. */
export interface ViewMeld {
  readonly kind: MahjongMeld['kind'];
  readonly concealed: boolean;
  /** The tiles; empty for somebody else's concealed kong. */
  readonly tiles: readonly Face[];
}

/**
 * Everything one seat may see: its own hand, how many tiles the others hold, every meld (a concealed kong of another seat is face-down),
 * every discard (public), the flowers set aside, how many tiles are left in the wall, and what the viewer may do now. No other hand and no wall
 * order is ever included; once the hand is over, `revealed` shows every hand (a win is shown to the table).
 */
export interface MahjongView {
  readonly viewer: Seat;
  readonly phase: MahjongPhase;
  readonly controllers: BridgeControllers;
  readonly dealer: Seat;
  readonly roundWind: MahjongWind;
  readonly seatWinds: Readonly<Record<Seat, MahjongWind>>;
  readonly flowers: boolean;
  readonly hand: readonly Face[];
  readonly handSizes: Readonly<Record<Seat, number>>;
  readonly melds: Readonly<Record<Seat, readonly ViewMeld[]>>;
  readonly bonus: Readonly<Record<Seat, readonly Face[]>>;
  readonly discards: readonly Discarded[];
  readonly wallCount: number;
  /** Whose turn it is to draw and discard. */
  readonly current: Seat;
  /** Who must act now. */
  readonly turn: Seat;
  readonly canAct: boolean;
  /** The tile just discarded and waiting for claims, and who threw it. */
  readonly pending: { readonly tile: Face; readonly from: Seat } | null;
  /** The tile the viewer just drew, or null. */
  readonly drawn: Face | null;
  /** In the discard phase: tiles the viewer may discard, a self-drawn win, and kong tiles. */
  readonly discardable: readonly Face[];
  readonly canWin: boolean;
  readonly kongTiles: readonly Face[];
  /** In the claim phase, when the viewer decides: what it may claim. */
  readonly claim: ClaimOptions | null;
  readonly result: MahjongResult | null;
  readonly revealed: Readonly<Record<Seat, readonly Face[]>> | null;
}

const hideMeld = (m: MahjongMeld, own: boolean): ViewMeld => ({ kind: m.kind, concealed: m.concealed, tiles: m.concealed && !own ? [] : m.tiles });

export function mahjongViewFor(game: MahjongGame, viewer: Seat): MahjongView {
  const mine = game.phase !== 'done' && game.turn === viewer;
  const discardPhase = mine && game.phase === 'discard';
  return {
    viewer,
    phase: game.phase,
    controllers: game.controllers,
    dealer: game.dealer,
    roundWind: game.roundWind,
    seatWinds: Object.fromEntries(BRIDGE_SEATS.map((s) => [s, seatWind(game, s)])) as unknown as Record<Seat, MahjongWind>,
    flowers: game.flowers,
    hand: game.hands[viewer],
    handSizes: Object.fromEntries(BRIDGE_SEATS.map((s) => [s, game.hands[s].length])) as unknown as Record<Seat, number>,
    melds: Object.fromEntries(BRIDGE_SEATS.map((s) => [s, game.melds[s].map((m) => hideMeld(m, s === viewer))])) as unknown as Record<Seat, readonly ViewMeld[]>,
    bonus: game.bonus,
    discards: game.discards,
    wallCount: game.wall.length,
    current: game.current,
    turn: game.turn,
    canAct: mine,
    pending: game.pending ? { tile: game.pending.tile, from: game.pending.from } : null,
    drawn: game.current === viewer && game.phase === 'discard' ? game.lastDrawn : null,
    discardable: discardPhase ? mahjongDiscardable(game) : [],
    canWin: discardPhase && canSelfWin(game),
    kongTiles: discardPhase ? kongTiles(game) : [],
    claim: mine && game.phase === 'claim' ? currentClaimOptions(game) : null,
    result: game.result,
    revealed: game.phase === 'done' ? game.hands : null,
  };
}
